import { apiClient } from './api-client';
import type { 
  OrganizationInvite,
  InviteStatus,
  AcceptOrganizationInviteDTO 
} from '@scrambl/shared';
import { createLogger } from '@/lib/logger';

const logger = createLogger('lib/api/organization-invites.api');

// Define response types for invite data
export interface OrganizationInviteData {
  organizationId: string;
organizationName?: string;
  organizationLogo?: string;
  email?: string;
  name?: string;
  status: InviteStatus;
  expiresAt?: string;
  token?: string;
  id: string;
  role?: string;
  createdAt?: string;
  invitedBy?: string;
  inviter?: {
    id: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    email: string;
  };
  organization?: {
    id: string;
    name: string;
    logo?: string | null;
    slug?: string;
  };
}

export interface BulkInviteResult {
  email: string;
  status: 'success' | 'error';
  invite?: OrganizationInviteData;
  error?: string;
}

interface InviteActionResponse {
  success: boolean;
  message?: string;
  invite: OrganizationInvite;
}

export const organizationInvitesApi = {
  // Get invite details by token
  async getByToken(token: string): Promise<OrganizationInviteData> {
    try {
      return await apiClient<OrganizationInviteData>(`/organization-invites/token/${token}`);
    } catch (error) {
      logger.error('[InvitesApi] Error fetching invite by token:', error);
      throw error;
    }
  },

  // Get all invites for the current user's email
  async getByEmail(email: string): Promise<{ data: OrganizationInviteData[] }> {
    try {
      return await apiClient<{ data: OrganizationInviteData[] }>(`/organization-invites/email/${encodeURIComponent(email)}`);
    } catch (error) {
      logger.error('[InvitesApi] Error fetching invites by email:', error);
      throw error;
    }
  },
  
  // Get all invites for an organization
  async getByOrganization(
    organizationId: string,
    status?: string,
    skip?: number,
    take?: number
  ): Promise<{ data: OrganizationInviteData[]; meta: { total: number } }> {
    try {
      let url = `/organization-invites/organization/${organizationId}`;
      
      // Add query parameters if provided
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (skip !== undefined) params.append('skip', skip.toString());
      if (take !== undefined) params.append('take', take.toString());
      
      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;
      
      return await apiClient<{ 
        data: OrganizationInviteData[]; 
        meta: { total: number } 
      }>(url);
    } catch (error) {
      logger.error('[InvitesApi] Error fetching invites for organization:', error);
      throw error;
    }
  },
  
  // Resend an invite
  async resendInvite(inviteId: string): Promise<OrganizationInviteData> {
    try {
      return await apiClient<OrganizationInviteData>(`/organization-invites/${inviteId}/resend`, {
        method: 'POST'
      });
    } catch (error) {
      logger.error('[InvitesApi] Error resending invite:', error);
      throw error;
    }
  },

  // Cancel/Revoke an invite
  async cancelInvite(inviteId: string): Promise<OrganizationInviteData> {
    try {
      return await apiClient<OrganizationInviteData>(`/organization-invites/${inviteId}/cancel`, {
        method: 'POST'
      });
    } catch (error) {
      logger.error('[InvitesApi] Error cancelling invite:', error);
      throw error;
    }
  },

  // Accept an invite with token
  async acceptInvite(token: string): Promise<InviteActionResponse> {
    try {
      return await apiClient<InviteActionResponse>('/organization-invites/accept', {
        method: 'POST',
        body: { token } satisfies AcceptOrganizationInviteDTO,
      });
    } catch (error) {
      logger.error('[InvitesApi] Error accepting invite:', error);
      throw error;
    }
  },

  // Decline an invite with token
  async declineInvite(token: string): Promise<InviteActionResponse> {
    try {
      return await apiClient<InviteActionResponse>('/organization-invites/decline', {
        method: 'POST',
        body: { token } satisfies AcceptOrganizationInviteDTO,
      });
    } catch (error) {
      logger.error('[InvitesApi] Error declining invite:', error);
      throw error;
    }
  },

  // Bulk create invites for an organization
  async bulkCreate(
    organizationId: string,
    invites: Array<{
      email: string;
      role?: 'ADMIN' | 'MEMBER';
      permissionSetId?: string;
      metadata?: Record<string, unknown>;
    }>
  ): Promise<BulkInviteResult[]> {
    try {
      // Clean up undefined values
      const cleanedInvites = invites.map(invite => {
        const cleanInvite: Record<string, unknown> = {
          email: invite.email,
          role: invite.role || 'MEMBER'
        };

        if (invite.permissionSetId && typeof invite.permissionSetId === 'string') {
          cleanInvite.permissionSetId = invite.permissionSetId;
        }

        if (invite.metadata) {
          cleanInvite.metadata = invite.metadata;
        }

        return cleanInvite;
      });

      return await apiClient<BulkInviteResult[]>(
        `/organization-invites/organization/${organizationId}/bulk`,
        {
          method: 'POST',
          body: { invites: cleanedInvites },
        }
      );
    } catch (error) {
      logger.error('[InvitesApi] Error creating bulk invites:', error);
      throw error;
    }
  }
}; 
